// Field-Level Security hook
// Provides field permissions for entities based on current user's role

import { useQuery } from '@tanstack/react-query'
import { apiRequest } from '../lib/api'

// Field access levels
export type FieldAccess = 'hidden' | 'read' | 'write'

// Entity types that support FLS
export type EntityType = 'leads' | 'accounts' | 'contacts' | 'opportunities' | 'events' | 'notes'

// FLS response for a single entity
interface FlsResponse {
  entity: EntityType
  role: string
  fields: Record<string, FieldAccess>
}

// FLS response for all entities
interface AllFlsResponse {
  role: string
  permissions: Record<EntityType, Record<string, FieldAccess>>
}

/**
 * Hook to get field permissions for a specific entity
 * Returns helpers to check read/write access per field
 */
export function useFls(entity: EntityType) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['fls', entity],
    queryFn: () => apiRequest<FlsResponse>(`/fls/${entity}`),
    staleTime: 10 * 60 * 1000, // 10 minutes - FLS config rarely changes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  })

  const fields = data?.fields || {}

  /**
   * Check if current user can read a field
   * Returns true if field is not hidden
   */
  const canRead = (field: string): boolean => {
    const access = fields[field] ?? 'write'
    return access !== 'hidden'
  }

  /**
   * Check if current user can write to a field
   * Returns true only if field has 'write' access
   */
  const canWrite = (field: string): boolean => {
    const access = fields[field] ?? 'write'
    return access === 'write'
  }

  /**
   * Get the access level for a field
   */
  const getAccess = (field: string): FieldAccess => {
    return fields[field] ?? 'write'
  }

  /**
   * Filter an array of fields to only readable ones
   * Useful for building column lists
   */
  const filterReadable = (fieldList: string[]): string[] => {
    return fieldList.filter(canRead)
  }

  /**
   * Filter an array of fields to only writable ones
   * Useful for building editable form fields
   */
  const filterWritable = (fieldList: string[]): string[] => {
    return fieldList.filter(canWrite)
  }

  /**
   * Get all hidden fields
   */
  const getHiddenFields = (): string[] => {
    return Object.entries(fields)
      .filter(([_, access]) => access === 'hidden')
      .map(([field]) => field)
  }

  /**
   * Get all read-only fields
   */
  const getReadOnlyFields = (): string[] => {
    return Object.entries(fields)
      .filter(([_, access]) => access === 'read')
      .map(([field]) => field)
  }

  return {
    // Raw data
    fields,
    role: data?.role,
    isLoading,
    error,

    // Helper functions
    canRead,
    canWrite,
    getAccess,
    filterReadable,
    filterWritable,
    getHiddenFields,
    getReadOnlyFields,
  }
}

/**
 * Hook to get field permissions for all entities at once
 * Useful for preloading FLS config on app init
 */
export function useAllFls() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['fls', 'all'],
    queryFn: () => apiRequest<AllFlsResponse>('/fls'),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  })

  /**
   * Get FLS helpers for a specific entity
   */
  const forEntity = (entity: EntityType) => {
    const fields = data?.permissions[entity] || {}

    return {
      canRead: (field: string) => (fields[field] ?? 'write') !== 'hidden',
      canWrite: (field: string) => (fields[field] ?? 'write') === 'write',
      getAccess: (field: string) => fields[field] ?? 'write',
    }
  }

  return {
    permissions: data?.permissions,
    role: data?.role,
    isLoading,
    error,
    forEntity,
  }
}

/**
 * Hook to prefetch FLS data for an entity
 * Call this early to warm the cache
 */
export function usePrefetchFls(entity: EntityType) {
  // This just triggers the query to load into cache
  useFls(entity)
}
